import { PropsWithChildren, ReactNode, createContext, useMemo, useState } from 'react';
import { DrawerProps } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import Drawer from '@/components/content/drawer/Drawer';
import { POSTHOG_EVENTS } from '@/lib/posthog/events';
import { trackEvent } from '@/lib/posthog/helpers';

interface DrawerContextValue {
  openDrawer: (options: MercuryDrawerProps) => void;
  closeDrawer: VoidFunction;
  opened: boolean;
  drawerid: any;
}

type MercuryDrawerProps = Omit<DrawerProps, 'title' | 'opened' | 'onClose'> & {
  title?: string;
  drawerid: any;
  link?: ReactNode;
  zIndex?: number;
};

export const DrawerContext = createContext<DrawerContextValue | null>(null);

export const DrawerProvider = ({ children }: PropsWithChildren<{}>) => {
  const [opened, { close, open }] = useDisclosure(false);
  const [drawerState, setDrawerState] = useState<MercuryDrawerProps>();

  const openDrawer = (options: MercuryDrawerProps) => {
    const payload = {
      source: window.location.pathname,
      drawerid: String(options.drawerid ?? ''),
    };

    if (opened) {
      trackEvent(POSTHOG_EVENTS.SIDEPANEL_ENTITY_NAVIGATE, payload);
    } else {
      trackEvent(POSTHOG_EVENTS.SIDEPANEL_OPEN, payload);
    }

    open();

    if (options) {
      setDrawerState(options);
    }
  };

  const closeDrawer = () => {
    trackEvent(POSTHOG_EVENTS.SIDEPANEL_CLOSE, {
      source: window.location.pathname,
      drawerid: String(drawerState?.drawerid ?? ''),
    });

    close();

    setDrawerState((state) => ({
      ...state,
      title: '',
      drawerid: null,
    }));
  };

  // eslint-disable-next-line react/jsx-no-constructed-context-values
  const value = useMemo(
    () => ({
      openDrawer,
      drawerid: drawerState?.drawerid,
      opened,
      closeDrawer,
    }),
    [opened, drawerState?.id, drawerState?.drawerid, drawerState?.children]
  );

  return (
    <DrawerContext.Provider value={value}>
      <Drawer opened={opened} onClose={closeDrawer} {...drawerState} />
      {children}
    </DrawerContext.Provider>
  );
};
